import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { ShoppingBag, User as UserIcon, Heart, LogOut, Menu, X, Search } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { LanguageToggle } from "@/components/LanguageToggle";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    navigate(`/shop?q=${encodeURIComponent(search)}`);
    setMobileOpen(false);
  };

  return (
    <header className="glass-header sticky top-0 z-50 border-b border-edge" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="font-serif text-3xl tracking-tight font-semibold">
              <span className="text-olive">OliveOil</span><span className="text-ink-muted/70 font-normal">.shop</span>
            </span>
            <span className="overline hidden sm:inline">{t("nav.tagline")}</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm">
            <Link to="/shop" className="hover:text-olive transition-colors" data-testid="nav-shop">{t("nav.shop")}</Link>
            <Link to="/vendors" className="hover:text-olive transition-colors" data-testid="nav-vendors">{t("nav.producers")}</Link>
            <Link to="/become-vendor" className="hover:text-olive transition-colors" data-testid="nav-become-vendor">{t("nav.sellWithUs")}</Link>
          </nav>

          <form onSubmit={onSearch} className="hidden lg:flex items-center gap-2 bg-white/70 hairline rounded-sm px-3 py-2 w-72">
            <Search className="w-4 h-4 text-ink-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("nav.searchPlaceholder")}
              className="bg-transparent outline-none text-sm w-full"
              data-testid="navbar-search-input"
            />
          </form>

          <div className="flex items-center gap-4">
            <LanguageToggle className="hidden md:flex" />
            <Link to="/wishlist" className="hidden sm:block text-ink-soft hover:text-olive" data-testid="nav-wishlist">
              <Heart className="w-5 h-5" />
            </Link>
            <Link to="/cart" className="relative text-ink-soft hover:text-olive" data-testid="nav-cart">
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-terracotta text-white text-[10px] font-semibold rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                  {count}
                </span>
              )}
            </Link>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 text-ink-soft hover:text-olive" data-testid="user-menu-trigger">
                    <UserIcon className="w-5 h-5" />
                    <span className="hidden sm:inline text-sm">{user.name.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-sm border-edge bg-white shadow-[0_8px_30px_rgba(44,42,41,0.12)]">
                  <DropdownMenuLabel className="text-xs text-ink-muted">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild><Link to="/account" data-testid="menu-account">{t("nav.myOrders")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/subscriptions" data-testid="menu-subscriptions">{t("nav.subscriptions")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/wishlist" data-testid="menu-wishlist">{t("nav.wishlist")}</Link></DropdownMenuItem>
                  {user.role === "vendor" && (
                    <DropdownMenuItem asChild><Link to="/vendor" data-testid="menu-vendor-dashboard">{t("nav.vendorDashboard")}</Link></DropdownMenuItem>
                  )}
                  {user.role === "buyer" && (
                    <DropdownMenuItem asChild><Link to="/become-vendor" data-testid="menu-become-vendor">{t("nav.becomeProducer")}</Link></DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild><Link to="/admin" data-testid="menu-admin">{t("nav.adminPanel")}</Link></DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />{t("nav.signOut")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" className="text-sm hover:text-olive" data-testid="nav-login">{t("nav.signIn")}</Link>
            )}
            <button className="md:hidden text-ink-soft" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-6 space-y-4 border-t border-edge pt-4">
            <form onSubmit={onSearch} className="flex items-center gap-2 bg-white hairline rounded-sm px-3 py-2">
              <Search className="w-4 h-4 text-ink-muted" />
              <input
                type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder={t("nav.searchPlaceholder")} className="bg-transparent outline-none text-sm w-full"
                data-testid="mobile-search-input"
              />
            </form>
            <Link to="/shop" className="block text-base" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-shop">{t("nav.shop")}</Link>
            <Link to="/vendors" className="block text-base" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-vendors">{t("nav.producers")}</Link>
            <Link to="/become-vendor" className="block text-base" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-become-vendor">{t("nav.sellWithUs")}</Link>
            <LanguageToggle />
          </div>
        )}
      </div>
    </header>
  );
};
